import type { PublicProductList, PublicProductSummary } from '@repo/api-contracts';
import { useEffect, useState } from 'react';
import {
  STORE_PAGE_SIZE,
  type StoreProductsParams,
  useStoreProductsQuery,
} from './use-store-products-query';

export interface StoreProductsPageView {
  readonly products: readonly PublicProductSummary[];
  readonly totalCount: number | null;
  /** 화면에 보여줄 응답 기준 페이지 수 — 페이지네이션 표시용. */
  readonly pageCount: number | null;
  /** 현재 조건의 실제 응답 기준 페이지 수 — 범위 밖 번호 보정 판단용(placeholder 제외). */
  readonly freshPageCount: number | null;
  /**
   * 보여줄 목록이 없는 상태 — 첫 로드 중이거나, 다른 조건의 응답(placeholder)만 있거나,
   * 범위 밖 페이지 번호를 마지막 페이지로 보정하는 중이다.
   */
  readonly isPending: boolean;
  /** 보여줄 목록이 없는 실패 — 전면 오류로 다룬다. */
  readonly isFirstLoadError: boolean;
  /** 같은 조건의 직전 목록을 유지한 채의 실패 — 목록 위 인라인 오류로 다룬다. */
  readonly isPageLoadError: boolean;
  readonly refetch: () => void;
}

/** page 를 뺀 조건 시그니처 — 같은 목록의 페이지 이동인지, 다른 목록인지 구분한다. */
const filterSignatureOf = (params: StoreProductsParams): string =>
  JSON.stringify([
    params.categoryId ?? null,
    params.exhibitionId ?? null,
    params.minOrderQtyMax ?? null,
    params.unitPriceMin ?? null,
    params.unitPriceMax ?? null,
    params.colorFamilies === undefined ? null : [...params.colorFamilies],
    params.sort,
  ]);

const pageCountOf = (totalCount: number | null): number | null =>
  totalCount === null ? null : Math.ceil(totalCount / STORE_PAGE_SIZE);

/**
 * 범위 밖 페이지 번호의 응답 — 상품은 있는데(totalCount > 0) 이 페이지에는 없다.
 * "조건에 맞는 상품이 없습니다" 가 아니라 화면이 마지막 페이지로 되돌려야 할 상태다.
 */
const isBeyondLastPage = (data: PublicProductList): boolean =>
  data.items.length === 0 && data.totalCount > 0;

/**
 * 스토어 목록 화면 상태 — 쿼리 결과를 "무엇을 보여줄지"로 번역한다.
 * keepPreviousData 는 pending 에만 적용되어 새 페이지 fetch 가 실패하면 data 가
 * 사라진다 — 같은 조건의 마지막 성공 응답만 보존해 목록을 유지하고,
 * 다른 조건의 응답은 절대 현재 조건의 결과처럼 보여주지 않는다.
 */
export const useStoreProductsPage = (params: StoreProductsParams): StoreProductsPageView => {
  const query = useStoreProductsQuery(params);
  const signature = filterSignatureOf(params);

  const [lastSuccess, setLastSuccess] = useState<
    { readonly signature: string; readonly data: PublicProductList } | undefined
  >(undefined);

  useEffect(() => {
    // placeholder(직전 키의 응답)나 범위 밖 번호의 빈 응답을 현재 조건의 성공으로 오기록하지 않는다 —
    // 후자를 보존하면 보정된 페이지의 로드 실패 시 빈 목록이 "직전 목록" 으로 남는다.
    if (query.data !== undefined && !query.isPlaceholderData && !isBeyondLastPage(query.data)) {
      setLastSuccess({ signature, data: query.data });
    }
  }, [query.data, query.isPlaceholderData, signature]);

  // 현재 조건과 같은 조건에서 마지막으로 성공한 응답.
  const preserved =
    lastSuccess !== undefined && lastSuccess.signature === signature ? lastSuccess.data : undefined;

  // 다른 조건의 placeholder 는 숨긴다 — 조건을 바꾼 직후 이전 조건의 목록이
  // 새 조건의 결과처럼 보이면 안 된다(로딩으로 취급).
  const isForeignPlaceholder = query.isPlaceholderData && preserved === undefined;
  const candidate = query.data ?? preserved;

  // 범위 밖 번호의 빈 응답(과 그것을 placeholder 로 쓰는 보정 중 구간)은 "상품 없음" 이 아니다 —
  // 화면이 마지막 페이지로 되돌릴 때까지 로딩으로 다룬다. freshPageCount 는 그대로 제공해 보정이 돈다.
  const isCorrectingPage = candidate !== undefined && isBeyondLastPage(candidate);
  const shownData = isForeignPlaceholder || isCorrectingPage ? undefined : candidate;

  const totalCount = shownData?.totalCount ?? null;

  return {
    products: shownData?.items ?? [],
    totalCount,
    pageCount: pageCountOf(totalCount),
    freshPageCount: query.isPlaceholderData ? null : pageCountOf(query.data?.totalCount ?? null),
    isPending: query.isPending || isForeignPlaceholder || isCorrectingPage,
    isFirstLoadError: query.isError && shownData === undefined,
    isPageLoadError: query.isError && shownData !== undefined,
    refetch: () => {
      void query.refetch();
    },
  };
};
